import { Box, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material"
import { DescriptionOutlined, MoreVertOutlined, PauseOutlined, SettingsOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import React, { useState } from "react"

import ProductVersionMenu from "./ProductVersionMenu"

import DisplayIds from "@/components/common/DisplayIds/DisplayIds"
import type { ProductVersionInfo } from "@/graphql/codegen/graphql"
import { ProductVersionStatus } from "@/graphql/codegen/graphql"
import { useProductActionsStore } from "@/screens/Companies/products/stores/useProductsActions"
import { ProductActionsDialogs } from "@/screens/Companies/products/types/schema"

interface ProductDetails {
  publicId?: string
  externalSystemId?: string | null
  isActive?: boolean
  productDescription?: string
  vendorDescription?: string
  vendorSku?: string
  companyName?: string
  productManager?: string
  classification?: string
  category?: string
  onToggleProductStatus: (isActive: boolean) => void
  productVersionId?: number
  productVersionStatus?: ProductVersionStatus
  productVersions: ProductVersionInfo[]
}

const ProductHeader: React.FC<ProductDetails> = (props: ProductDetails) => {
  const {
    publicId,
    externalSystemId,
    productDescription,
    isActive,
    vendorDescription,
    vendorSku,
    companyName,
    productManager,
    classification,
    productVersionId,
    productVersionStatus,
    category,
    onToggleProductStatus,

    productVersions,
  } = props

  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const setOpenedDialog = useProductActionsStore((state) => state.setOpenedDialog)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEditInformation = () => {
    setOpenedDialog(ProductActionsDialogs.EDIT_PRODUCT_INFORMATION)
    handleClose()
  }

  const handleEditSpecifics = () => {
    setOpenedDialog(ProductActionsDialogs.EDIT_PRODUCT_SPECIFICS)
    handleClose()
  }

  const handleToggleProductStatus = () => {
    onToggleProductStatus(Boolean(isActive))
    handleClose()
  }

  const handleSelectProductVersion = (versionInfo: ProductVersionInfo) => {
    navigate({
      search: (prev: Record<string, any>) => ({
        ...prev,
        version: versionInfo.versionNumber,
      }),
      replace: true,
    })
  }

  return (
    <Box className='rounded-md border border-solid border-gray-300 p-6'>
      <Box className='flex items-center justify-between gap-3'>
        <Box className='flex items-center gap-3'>
          <Typography variant='overline' className='tracking-[1px] text-gray-700'>
            AKROCHEM DESCRIPTION
          </Typography>
        </Box>
        {productVersionStatus === ProductVersionStatus.Active && (
          <IconButton
            color='primary'
            aria-label='edit product options'
            onClick={handleClick}
            aria-controls={open ? "product-edit-menu" : undefined}
            aria-haspopup
            aria-expanded={open ? "true" : undefined}
            data-testid='product-edit-icon-btn'
          >
            <MoreVertOutlined />
          </IconButton>
        )}
        <Menu
          id='product-edit-menu'
          data-testid='product-edit-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "product-edit-button",
          }}
        >
          <MenuItem onClick={handleEditInformation}>
            <ListItemIcon>
              <DescriptionOutlined />
            </ListItemIcon>
            Edit Product Information
          </MenuItem>
          <MenuItem onClick={handleEditSpecifics}>
            <ListItemIcon>
              <SettingsOutlined />
            </ListItemIcon>
            Edit Product Specifics
          </MenuItem>

          <MenuItem onClick={handleToggleProductStatus}>
            <ListItemIcon>
              <PauseOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary={isActive ? "Deactivate Product" : "Activate"} />
          </MenuItem>
        </Menu>
      </Box>
      <Box className='flex items-center gap-3'>
        <Typography variant='h5' className='text-gray-800'>
          {productDescription}
        </Typography>

        {productVersionStatus === ProductVersionStatus.Draft ? (
          <Chip label='Draft' variant='outlined' color='warning' />
        ) : (
          <Chip label={isActive ? "Active" : "Pending"} variant='outlined' color={isActive ? "success" : "warning"} />
        )}

        <ProductVersionMenu
          productVersionId={productVersionId}
          productVersions={productVersions}
          displayDraft
          onSelectProductVersion={handleSelectProductVersion}
        />

        <DisplayIds entityName='Product ID' publicId={publicId} externalSystemId={externalSystemId} />
      </Box>
      <Box className='mt-6 flex items-center gap-6'>
        <Typography variant='body2' className='font-medium tracking-[0.46px] text-gray-700'>
          Vendor Description: {vendorDescription}
        </Typography>
        <Typography variant='body2' className='font-medium tracking-[0.46px] text-gray-700'>
          Vendor SKU: {vendorSku}
        </Typography>
        <Typography variant='body2' className='font-medium tracking-[0.46px] text-gray-700'>
          Vendor: {companyName}
        </Typography>
      </Box>
      <Box className='mt-6 flex items-center gap-6'>
        <Box className='flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Category:
          </Typography>
          <Chip label={category} className='rounded-sm text-xs' />
        </Box>
        <Box className='flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Class:
          </Typography>
          <Chip label={classification} className='rounded-sm text-xs' />
        </Box>
        <Box className='flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Product Manager:
          </Typography>
          {productManager ? <Chip label={productManager} className='rounded-sm bg-tertiary-yellow-100 text-xs' /> : "-"}
        </Box>
      </Box>
    </Box>
  )
}

export default ProductHeader
